import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Settings, Save, Shield } from 'lucide-react';

export default function QualityAdmin() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [selected, setSelected] = useState(null);
  const [edits, setEdits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([api.get('/quality/display-profiles'), api.get('/patients/02bff798-a454-4457-9d44-4d04b5c44c1d/quality-overview')]);
      setProfiles(pRes.data.profiles || []);
      const allMeasures = [...(mRes.data.sections?.must_address_today || []), ...(mRes.data.sections?.optional_opportunities || []), ...(mRes.data.sections?.suppressed || []), ...(mRes.data.sections?.deferred || [])];
      setMeasures(allMeasures);
      if (pRes.data.profiles?.length && !selected) {
        const p = pRes.data.profiles[0];
        setSelected(p);
        setEdits({ ...p });
      }
    } catch { toast.error('Failed to load profiles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleMeasure = (mid) => {
    if (!edits) return;
    const enabled = new Set(edits.enabled_measure_ids || []);
    enabled.has(mid) ? enabled.delete(mid) : enabled.add(mid);
    setEdits({ ...edits, enabled_measure_ids: [...enabled] });
  };

  const handleSave = async () => {
    if (!edits?.id) return;
    setSaving(true);
    try {
      const { id, ...updates } = edits;
      await api.put(`/quality/display-profiles/${id}`, updates);
      toast.success('Profile saved');
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex-1 p-4"><Skeleton className="h-64 w-full" /></div>;

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 max-w-4xl" data-testid="quality-admin">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Settings className="h-5 w-5" />Quality Display Settings</h1>
        <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => navigate(-1)}><ArrowLeft className="h-3 w-3" />Back</Button>
      </div>

      {edits && (
        <>
          <Card className="border">
            <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Profile: {edits.name}</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between"><span className="text-xs">Annual Visit Max Cards</span><Input type="number" className="w-20 h-7 text-xs" value={edits.annual_visit_max_cards || 6} onChange={e => setEdits({ ...edits, annual_visit_max_cards: Number(e.target.value) })} /></div>
              <div className="flex items-center justify-between"><span className="text-xs">Non-Annual Max Cards</span><Input type="number" className="w-20 h-7 text-xs" value={edits.non_annual_max_cards || 2} onChange={e => setEdits({ ...edits, non_annual_max_cards: Number(e.target.value) })} /></div>
              <div className="flex items-center justify-between"><span className="text-xs">Show Optional Opportunities</span><Switch checked={edits.show_optional_opportunities !== false} onCheckedChange={v => setEdits({ ...edits, show_optional_opportunities: v })} /></div>
            </CardContent>
          </Card>

          <Card className="border">
            <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Enabled Measures</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-2">
              {["qm_crc_screening", "qm_breast_screening", "qm_depression_screening", "qm_bp_control", "qm_diabetes_a1c", "qm_acp_candidate"].map(mid => (
                <div key={mid} className="flex items-center justify-between py-1 border-b last:border-0">
                  <span className="text-xs">{mid.replace(/qm_/g, '').replace(/_/g, ' ')}</span>
                  <Switch checked={(edits.enabled_measure_ids || []).includes(mid)} onCheckedChange={() => toggleMeasure(mid)} data-testid={`toggle-${mid}`} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Button size="sm" className="gap-1" onClick={handleSave} disabled={saving} data-testid="save-profile"><Save className="h-3 w-3" />{saving ? 'Saving...' : 'Save Profile'}</Button>
        </>
      )}
    </div>
  );
}
